import { UseFormReturn } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { cn } from '@/lib/utils';
import type { EventFormData } from './eventFormSchema';

interface LocationSectionProps {
  form: UseFormReturn<EventFormData>;
}

/**
 * Simplified location section with radio buttons instead of toggle switch.
 * - "Hos gården" (default) → use_producer_address = true
 * - "Annan plats" → use_producer_address = false + custom_address input
 */
export function LocationSection({ form }: LocationSectionProps) {
  const useProducerAddress = form.watch('use_producer_address');

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => {
            form.setValue('use_producer_address', true, { shouldDirty: true });
            form.setValue('custom_address', '', { shouldDirty: true });
          }}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg border p-3 text-left text-sm transition-colors',
            useProducerAddress
              ? 'border-primary bg-primary/5 ring-1 ring-primary'
              : 'border-border hover:border-primary/40'
          )}
        >
          <span
            className={cn(
              'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
              useProducerAddress ? 'border-primary' : 'border-muted-foreground/40'
            )}
          >
            {useProducerAddress && (
              <span className="h-2 w-2 rounded-full bg-primary" />
            )}
          </span>
          <span className={cn(useProducerAddress ? 'font-medium' : 'text-muted-foreground')}>
            Hos värden
          </span>
        </button>

        <button
          type="button"
          onClick={() => {
            form.setValue('use_producer_address', false, { shouldDirty: true });
          }}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg border p-3 text-left text-sm transition-colors',
            !useProducerAddress
              ? 'border-primary bg-primary/5 ring-1 ring-primary'
              : 'border-border hover:border-primary/40'
          )}
        >
          <span
            className={cn(
              'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
              !useProducerAddress ? 'border-primary' : 'border-muted-foreground/40'
            )}
          >
            {!useProducerAddress && (
              <span className="h-2 w-2 rounded-full bg-primary" />
            )}
          </span>
          <span className={cn(!useProducerAddress ? 'font-medium' : 'text-muted-foreground')}>
            Annan plats
          </span>
        </button>
      </div>

      {!useProducerAddress && (
        <FormField
          control={form.control}
          name="custom_address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Adress</FormLabel>
              <FormControl>
                <Input placeholder="Ange adress för aktiviteten" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}
